'use client';

/**
 * HelpPanel Component
 *
 * A slide-out panel that provides contextual help and documentation.
 * Can be triggered by a floating help button or programmatically.
 * Supports searchable articles, video embeds, and contact options.
 */

import ArticleIcon from '@mui/icons-material/Article';
import CloseIcon from '@mui/icons-material/Close';
import ContactIcon from '@mui/icons-material/ContactSupport';
import HelpIcon from '@mui/icons-material/HelpOutlined';
import ArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import VideoIcon from '@mui/icons-material/OndemandVideo';
import SearchIcon from '@mui/icons-material/Search';
import {
  alpha,
  Box,
  Chip,
  Drawer,
  Fab,
  IconButton,
  InputAdornment,
  InputBase,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useCallback, useMemo, useState } from 'react';
import { useI18n } from '../../hooks/useI18n';
import { glassEffects } from '../../theme/shadows';
import { iconSizes } from '../../theme/typography';

// ============================================================================
// Types
// ============================================================================

export type HelpArticleType = 'article' | 'video' | 'guide';

export interface HelpArticle {
  /** Unique identifier */
  id: string;
  /** Article title */
  title: string;
  /** Short description */
  description?: string;
  /** Article type */
  type: HelpArticleType;
  /** Tags for categorization */
  tags?: string[];
  /** URL to the full article or video */
  url?: string;
  /** Inline content (for simple articles) */
  content?: React.ReactNode;
  /** Related page routes (for context-aware filtering) */
  relatedRoutes?: string[];
  /** Video duration (for video type) */
  duration?: string;
  /** Reading time estimate */
  readingTime?: string;
  /**
   * Issue #356: Persona guide integration
   * Stable route key for navigation-aware linking
   */
  routeKey?: string;
  /**
   * Issue #356: Persona this article is relevant to
   * Used for persona-specific filtering
   */
  persona?: string;
  /**
   * Issue #356: Portal this article belongs to
   */
  portal?: string;
}

export interface HelpPanelSection {
  /** Section identifier */
  id: string;
  /** Section title */
  title: string;
  /** Articles in this section */
  articles: HelpArticle[];
}

export interface HelpPanelProps {
  /** Whether the panel is open */
  open: boolean;
  /** Callback when panel should close */
  onClose: () => void;
  /** Help sections */
  sections?: HelpPanelSection[];
  /** Current route (for context-aware filtering) */
  currentRoute?: string;
  /** Panel title */
  title?: string;
  /** Subtitle or description */
  subtitle?: string;
  /** Show floating help button */
  showFab?: boolean;
  /** FAB position */
  fabPosition?: { bottom: number; right: number };
  /** Callback when FAB is clicked */
  onFabClick?: () => void;
  /** Custom header content */
  headerContent?: React.ReactNode;
  /** Custom footer content (e.g., contact support link) */
  footerContent?: React.ReactNode;
  /** Whether to filter by current route */
  filterByRoute?: boolean;
  /** Callback when article is selected */
  onArticleSelect?: (article: HelpArticle) => void;
  /** Panel width */
  width?: number | string;
  /** Anchor position */
  anchor?: 'left' | 'right';
}

// ============================================================================
// Styled Components
// ============================================================================

const StyledDrawer = styled(Drawer, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<{ $isDark: boolean; $width: number | string }>(({ $isDark, $width }) => {
  const glass = $isDark ? glassEffects.dark : glassEffects.light;

  return {
    '& .MuiDrawer-paper': {
      width: $width,
      maxWidth: '100vw',
      background: $isDark ? 'rgba(13, 33, 55, 0.98)' : 'rgba(255, 255, 255, 0.98)',
      backdropFilter: glass.backdropBlur,
      borderLeft: `1px solid ${glass.border}`,
      boxShadow: '-8px 0 32px rgba(0, 0, 0, 0.15)',
    },
  };
});

const Header = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 2.5),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const SearchContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0.75, 1.5),
  borderRadius: 8,
  backgroundColor:
    theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.05) : alpha(theme.palette.common.black, 0.04),
  border: `1px solid ${theme.palette.divider}`,
  marginTop: theme.spacing(2),

  '&:focus-within': {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
  },
}));

const ContentArea = styled(Box)({
  flex: 1,
  overflowY: 'auto',
});

const SectionHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5, 2.5),
  backgroundColor:
    theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.02) : alpha(theme.palette.common.black, 0.02),
}));

const StyledListItemButton = styled(ListItemButton)(({ theme }) => ({
  margin: theme.spacing(0.5, 1),
  borderRadius: 8,
  padding: theme.spacing(1.25, 1.5),
  transition: 'all 100ms ease',

  '&:hover': {
    backgroundColor:
      theme.palette.mode === 'dark' ? alpha(theme.palette.primary.main, 0.1) : alpha(theme.palette.primary.main, 0.06),
  },
}));

const Footer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 2.5),
  borderTop: `1px solid ${theme.palette.divider}`,
}));

const StyledFab = styled(Fab)(({ theme }) => ({
  boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.4)}`,

  '&:hover': {
    boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.5)}`,
  },
}));

const TypeIcon: React.FC<{ type: HelpArticleType }> = ({ type }) => {
  switch (type) {
    case 'video':
      return <VideoIcon />;
    case 'guide':
      return <ArticleIcon />;
    case 'article':
    default:
      return <ArticleIcon />;
  }
};

// ============================================================================
// Component
// ============================================================================

export const HelpPanel: React.FC<HelpPanelProps> = ({
  open,
  onClose,
  sections = [],
  currentRoute,
  title,
  subtitle,
  showFab = false,
  fabPosition = { bottom: 24, right: 24 },
  onFabClick,
  headerContent,
  footerContent,
  filterByRoute = false,
  onArticleSelect,
  width = 380,
  anchor = 'right',
}) => {
  const theme = useTheme();
  const { t } = useI18n();
  const isDark = theme.palette.mode === 'dark';
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedArticle, setExpandedArticle] = useState<string | null>(null);

  // Use i18n for default values
  const displayTitle = title ?? t('help.title');
  const displaySubtitle = subtitle ?? t('help.subtitle');

  // Filter sections based on search query and current route
  const filteredSections = useMemo(() => {
    return sections
      .map((section) => {
        let articles = section.articles;

        // Filter by route if enabled
        if (filterByRoute && currentRoute) {
          articles = articles.filter(
            (article) => !article.relatedRoutes || article.relatedRoutes.some((r) => currentRoute.startsWith(r)),
          );
        }

        // Filter by search query
        if (searchQuery.trim()) {
          const query = searchQuery.toLowerCase();
          articles = articles.filter(
            (article) =>
              article.title.toLowerCase().includes(query) ||
              article.description?.toLowerCase().includes(query) ||
              article.tags?.some((tag) => tag.toLowerCase().includes(query)),
          );
        }

        return { ...section, articles };
      })
      .filter((section) => section.articles.length > 0);
  }, [sections, searchQuery, currentRoute, filterByRoute]);

  const handleArticleClick = useCallback(
    (article: HelpArticle) => {
      if (article.content) {
        setExpandedArticle(expandedArticle === article.id ? null : article.id);
      } else if (article.url) {
        window.open(article.url, '_blank', 'noopener,noreferrer');
      }
      onArticleSelect?.(article);
    },
    [expandedArticle, onArticleSelect],
  );

  const renderArticle = (article: HelpArticle) => {
    const isExpanded = expandedArticle === article.id;

    return (
      <ListItem key={article.id} disablePadding sx={{ flexDirection: 'column', alignItems: 'stretch' }}>
        <StyledListItemButton onClick={() => handleArticleClick(article)}>
          <ListItemIcon sx={{ minWidth: 36, color: 'text.secondary' }}>
            <TypeIcon type={article.type} />
          </ListItemIcon>
          <ListItemText
            primary={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                  }}
                >
                  {article.title}
                </Typography>
                {article.type === 'video' && article.duration && (
                  <Chip
                    size="small"
                    label={article.duration}
                    sx={{ height: 18, fontSize: '0.75rem', bgcolor: 'action.hover' }}
                  />
                )}
              </Box>
            }
            secondary={article.description}
            slotProps={{
              secondary: {
                variant: 'caption',
                sx: { opacity: 0.7, mt: 0.25 },
              },
            }}
          />
          <ArrowRightIcon
            sx={{
              opacity: 0.5,
              fontSize: iconSizes.md,
              transition: 'transform 150ms',
              transform: isExpanded ? 'rotate(90deg)' : 'none',
            }}
          />
        </StyledListItemButton>
        {/* Expandable inline content */}
        <AnimatePresence>
          {isExpanded && article.content && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.15 }}
              style={{ overflow: 'hidden' }}
            >
              <Box
                sx={{
                  mx: 2,
                  mb: 1,
                  p: 2,
                  borderRadius: 1,
                  bgcolor: isDark ? alpha(theme.palette.common.white, 0.03) : alpha(theme.palette.common.black, 0.02),
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                <Typography
                  variant="body2"
                  component="div"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  {article.content}
                </Typography>
              </Box>
            </motion.div>
          )}
        </AnimatePresence>
      </ListItem>
    );
  };

  return (
    <>
      {/* Floating Help Button */}
      {showFab && !open && (
        <StyledFab
          color="primary"
          aria-label={t('a11y.openHelp')}
          onClick={onFabClick}
          sx={{
            position: 'fixed',
            bottom: fabPosition.bottom,
            right: fabPosition.right,
            zIndex: theme.zIndex.speedDial,
          }}
        >
          <HelpIcon />
        </StyledFab>
      )}
      {/* Help Panel Drawer */}
      <StyledDrawer
        anchor={anchor}
        open={open}
        onClose={onClose}
        $isDark={isDark}
        $width={width}
        ModalProps={{ keepMounted: true }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Header */}
          <Header>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                  }}
                >
                  {displayTitle}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    mt: 0.5,
                  }}
                >
                  {displaySubtitle}
                </Typography>
              </Box>
              <IconButton onClick={onClose} size="small" sx={{ ml: 1 }}>
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Search */}
            <SearchContainer>
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.secondary', fontSize: iconSizes.base }} />
              </InputAdornment>
              <InputBase
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('help.search')}
                fullWidth
                sx={{ ml: 1, fontSize: '0.875rem' }}
              />
            </SearchContainer>

            {headerContent}
          </Header>

          {/* Content */}
          <ContentArea>
            {filteredSections.length === 0 && (
              <Box sx={{ py: 6, textAlign: 'center' }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  {searchQuery ? t('help.noResults') : t('help.empty')}
                </Typography>
              </Box>
            )}

            {filteredSections.map((section) => (
              <Box key={section.id}>
                <SectionHeader>
                  <Typography variant="overline" sx={{ fontWeight: 600, opacity: 0.7 }}>
                    {section.title}
                  </Typography>
                </SectionHeader>
                <List dense disablePadding>
                  {section.articles.map(renderArticle)}
                </List>
              </Box>
            ))}
          </ContentArea>

          {/* Footer */}
          {footerContent ? (
            <Footer>{footerContent}</Footer>
          ) : (
            <Footer>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: isDark ? alpha(theme.palette.info.main, 0.1) : alpha(theme.palette.info.main, 0.06),
                  cursor: 'pointer',
                  transition: 'background-color 150ms',
                  '&:hover': {
                    bgcolor: isDark ? alpha(theme.palette.info.main, 0.15) : alpha(theme.palette.info.main, 0.1),
                  },
                }}
              >
                <ContactIcon sx={{ color: 'info.main', fontSize: iconSizes.base }} />
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                    }}
                  >
                    {t('help.needHelp')}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                    }}
                  >
                    {t('help.contactSupport')}
                  </Typography>
                </Box>
              </Box>
            </Footer>
          )}
        </Box>
      </StyledDrawer>
    </>
  );
};

HelpPanel.displayName = 'HelpPanel';

// ============================================================================
// Persona Guide Helpers (Issue #356)
// ============================================================================

/**
 * Persona guide metadata from retrieval service
 */
export interface PersonaGuideMetadata {
  persona: string;
  section: string;
  portal: string;
  routeKey?: string;
  keywords?: string[];
}

/**
 * Convert persona guide retrieval results to HelpArticle format
 *
 * Issue #356: Helper to integrate persona guides into the HelpPanel
 *
 * @param guides - Retrieval results with persona guide metadata
 * @param resolveUrl - Optional function to resolve route keys to URLs
 * @returns Array of HelpArticle items
 */
export function personaGuidesToHelpArticles(
  guides: Array<{
    id: string;
    title?: string;
    content: string;
    score: number;
    metadata: PersonaGuideMetadata;
  }>,
  resolveUrl?: (routeKey: string) => string | null,
): HelpArticle[] {
  return guides.map((guide) => ({
    id: guide.id,
    title: guide.title ?? guide.metadata.section,
    description: guide.content.slice(0, 150) + (guide.content.length > 150 ? '...' : ''),
    type: 'guide' as HelpArticleType,
    tags: guide.metadata.keywords ?? [guide.metadata.persona],
    url: guide.metadata.routeKey && resolveUrl ? (resolveUrl(guide.metadata.routeKey) ?? undefined) : undefined,
    routeKey: guide.metadata.routeKey,
    persona: guide.metadata.persona,
    portal: guide.metadata.portal,
    relatedRoutes: guide.metadata.routeKey ? [guide.metadata.routeKey] : undefined,
  }));
}

export default HelpPanel;
